import { Box, SvgIcon, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import { styled } from '@mui/material/styles';
import { ReactComponent as alertWarning } from 'assets/alertWarning.svg';
import { ReactComponent as redirectionArrow } from 'assets/redirectionArrow.svg';
import { Canceler } from 'axios';
import {
  ICandidateAwaiting,
  ICandidateAwaitingPayload,
  ICandidateAwaitingReviewData,
} from 'modules/hiring/models/candidateAwaitingReview.model';
import {
  HiringActivity,
  HMStatusListForInterview,
  HMStatusListForScreen,
} from 'modules/hiring/models/candidatePipeline.model';
import {
  getMakeDecisionButtonStatus,
  shouldMakeDecision,
  TMakeDecisionStatus,
} from 'modules/hiring/models/hiringDecisionOption';
import { HMRoutingPaths } from 'modules/hiring/models/HMRoutingPaths';
import { IRequisition } from 'modules/hiring/models/myOpenRequisition.model';
import { axiosCandidatesAwaitingReview } from 'modules/hiring/stateManagement/api';
import {
  getMyOpenRolesData,
  setSelectedOpenRequisition,
} from 'modules/hiring/stateManagement/thunks';
import {
  calculateDaysinStep,
  customEventSubscribe,
  customEventUnsubscribe,
} from 'modules/hiring/utilities';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import TypographyWithI18N from 'shared/components/TypographyWithI18N';
import Widget from 'shared/components/Widget';
import IStoreState from 'store/IStoreState';
import { colors } from 'styles/colors';
import './candidatesAwaitingReview.scss';
import CandidatesAwaitingReviewSkeleton from './candidatesAwaitingReviewSkeleton';

type TCandidatesAwaitingReviewStateProps = ReturnType<typeof mapStateToProps>;
type TCandidatesAwaitingReviewDispatchProps = {
  getMyOpenRolesData: () => void;
  setSelectedOpenRequisition: (requisition: IRequisition | null) => void;
};

interface ICandidatesAwaitingReviewProps
  extends TCandidatesAwaitingReviewStateProps,
    TCandidatesAwaitingReviewDispatchProps {}
const noOfLoadingCards = 3;
const Item = styled(Paper)(({ theme }) => ({
  ...theme.typography.body2,
  padding: '0.125rem 1rem',
  textAlign: 'left',
  color: theme.palette.text.secondary,
  borderRadius: '0.5rem',
  border: 'none',
}));

const CandidatesAwaitingReview: React.FC<ICandidatesAwaitingReviewProps> = ({
  myOpenRoles,
  getMyOpenRolesData: getMyOpenRoles,
  setSelectedOpenRequisition: setSelectedRequisition,
}) => {
  const { t } = useTranslation();
  const [candidateAwaitingData, setCandidateAwaitingData] = useState<
    ICandidateAwaitingReviewData['candidateAwaitList']
  >([]);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<ICandidateAwaiting[]>([]);
  let candidateAwaitingReviewcanceler: Canceler;
  const navigate = useNavigate();
  const { data: yourOpenRolesData } = myOpenRoles;
  const getCandidateAwaitingReview = async () => {
    const payload: ICandidateAwaitingPayload = {
      isAscending: true,
      sortName: '',
    };
    try {
      setLoading(true);
      const response: ICandidateAwaitingReviewData =
        await axiosCandidatesAwaitingReview(
          payload,
          (cancel: Canceler) => (candidateAwaitingReviewcanceler = cancel)
        );
      setData(
        () =>
          response?.candidateAwaitList?.filter(
            (e) => !e?.currentActivityStatus
          ) ?? []
      );
      setLoading(false);
    } catch (err) {
      throw err;
    }
  };

  useEffect(() => {
    getCandidateAwaitingReview();
    customEventSubscribe('makeDecision', getCandidateAwaitingReview);
    return () => {
      customEventUnsubscribe('makeDecision', getCandidateAwaitingReview);
      candidateAwaitingReviewcanceler?.();
    };
  }, []);

  useEffect(() => {
    /* istanbul ignore else */
    if (data !== null) {
      const candidateAwaitingDataSort = [...data];
      candidateAwaitingDataSort.sort(
        (a: ICandidateAwaiting, b: ICandidateAwaiting) => {
          const hireDateFirst = new Date(
            a.hiringManagerInterviewDate ?? ''
          ).getTime();
          const hireDateSecond = new Date(
            b.hiringManagerInterviewDate ?? ''
          ).getTime();
          if (hireDateFirst === hireDateSecond)
            return a.name.localeCompare(b.name);
          else if (hireDateFirst < hireDateSecond) return -1;
          else return 1;
        }
      );
      let candidateAwaitingFilteredData = candidateAwaitingDataSort.filter(
        (item) =>
          item?.activityStatus !== HMStatusListForInterview.Reject &&
          item?.activityStatus !== HMStatusListForScreen.Reject
      );
      setCandidateAwaitingData(candidateAwaitingFilteredData);
    }
  }, [data]);

  useEffect(() => {
    getMyOpenRoles();
    getCandidateAwaitingReview();
    customEventSubscribe('makeDecision', getCandidateAwaitingReview);
    return () => {
      customEventUnsubscribe('makeDecision', getCandidateAwaitingReview);
      candidateAwaitingReviewcanceler?.();
    };
  }, []);

  return (
    <Widget
      id='candidates-awaiting-review-widget'
      headingProps={{
        id: 'candidates-awaiting-review-heading',
        i18nKey: 'candidatesAwaitingReview',
      }}
      withDivider
    >
      <Stack
        spacing={2}
        sx={{
          py: ({ typography }) => typography.pxToRem(20),
          pr: ({ typography }) => typography.pxToRem(8),
          mr: ({ typography }) => typography.pxToRem(-16),
        }}
        className='innerContainer'
      >
        {loading ? (
          [...Array(noOfLoadingCards)].map((item, i) => (
            <Box
              id={`candidates-activity-loading-card-${i}`}
              data-testid={`candidates-activity-loading-card-${i}`}
              key={`box-${i}`}
            >
              <CandidatesAwaitingReviewSkeleton
                id={'awaiting-review-card-' + i}
                key={item + '_' + i}
              />
            </Box>
          ))
        ) : !data?.length ? (
          <TypographyWithI18N
            className='kfic2-bodyText'
            data-testid='no-candidates-awaiting-review'
            i18nKey='noCandidatesAwaitingReview'
          />
        ) : (
          candidateAwaitingData?.map((d, i) => {
            if (shouldMakeDecision(d?.activityName as HiringActivity)) {
              const hiringManagerInterviewDate = calculateDaysinStep(
                d?.hiringManagerInterviewDate ?? ''
              );
              const isHiringInterviewDateGT5 = hiringManagerInterviewDate > 5;
              return (
                <Item
                  key={`item-${i}`}
                  sx={{
                    border: `0.063rem solid ${colors.connector.border}`,
                    p: '0.75rem 1rem',
                    cursor: !getMakeDecisionButtonStatus(
                      d?.activityName as HiringActivity,
                      d?.currentActivityStatus as TMakeDecisionStatus
                    )
                      ? 'pointer'
                      : 'default',
                  }}
                >
                  <Grid
                    data-testid={`candidates-awaiting-review-card-${i}`}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                    onClick={() => {
                      if (
                        !getMakeDecisionButtonStatus(
                          d?.activityName as HiringActivity,
                          d?.currentActivityStatus as TMakeDecisionStatus
                        )
                      ) {
                        const reqId = String(d?.requisitionID);
                        const matchedReq =
                          yourOpenRolesData?.requisitionList?.find(
                            (r: IRequisition) => String(r.requisitionID) === reqId,
                          ) ?? null;
                        setSelectedRequisition(matchedReq);
                        navigate(`../${HMRoutingPaths.candidateProfile}`, {
                          state: {
                            candidate: { ...d, candidateID: d?.candidateId },
                          },
                        });
                      }
                    }}
                    columnGap={'0.75rem'}
                  >
                    <Grid
                      item
                      sx={{
                        flex: '1',
                        width: '85%',
                      }}
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                      }}
                    >
                      <Stack
                        sx={{
                          bgcolor: isHiringInterviewDateGT5
                            ? colors.background.redOrange
                            : colors.infoNeutral.main,
                          width: '60px',
                          height: '60px',
                          justifyContent: 'center',
                          alignItems: 'center',
                          borderRadius: '.875rem',
                          marginRight: '0.75rem',
                          flexShrink: 0,
                        }}
                      >
                        {isHiringInterviewDateGT5 ? (
                          <Box>
                            <SvgIcon
                              viewBox='0 0 24 24'
                              sx={{
                                width: 24,
                                height: 24,
                                alignSelf: 'center',
                              }}
                              component={alertWarning}
                            />
                          </Box>
                        ) : (
                          <>
                            <Typography className='kfic2-header'>
                              {hiringManagerInterviewDate}
                            </Typography>
                            <Typography className='kfic2-metadata'>
                              {hiringManagerInterviewDate > 1
                                ? t('days')
                                : t('day')}
                            </Typography>
                          </>
                        )}
                      </Stack>
                      <Stack>
                        <Typography
                          variant='h1'
                          className='kfic2-smallHeader'
                          style={{
                            textOverflow: 'ellipsis',
                            wordBreak: 'break-word',
                            overflow: 'auto',
                            whiteSpace: 'normal',
                            width: '100%',
                          }}
                        >
                          {d?.name}
                        </Typography>
                        <Typography
                          className='kfic2-bodyText'
                          style={{
                            textOverflow: 'ellipsis',
                            wordBreak: 'break-word',
                            overflow: 'auto',
                          }}
                        >
                          {t('appliedForSomeSome', {
                            arg1: d?.jobTitle,
                            arg2: d?.requisitionID,
                            interpolation: { escapeValue: false },
                          })}
                        </Typography>
                        {isHiringInterviewDateGT5 && (
                          <Typography
                            className='kfic2-metadata'
                            sx={{
                              color: `${colors.tertiary.redBrown} !important`,
                            }}
                          >
                            {t('someDaysAgo', {
                              arg1: hiringManagerInterviewDate,
                            })}
                          </Typography>
                        )}
                      </Stack>
                    </Grid>
                    <Grid item justifyContent={'end'}>
                      <SvgIcon
                        viewBox='0 0 20 20'
                        sx={{ width: 20, height: 20, alignSelf: 'center' }}
                        component={redirectionArrow}
                      />
                    </Grid>
                  </Grid>
                </Item>
              );
            }
            return null;
          })
        )}
      </Stack>
    </Widget>
  );
};

const mapStateToProps = (state: IStoreState) => ({
  myOpenRoles: state.myOpenRoles,
});

const mapDispatchToProps = {
  getMyOpenRolesData,
  setSelectedOpenRequisition,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(CandidatesAwaitingReview);
